import React, { useEffect, useState } from "react";
import { QRCode } from "react-qrcode-logo";


const TOTP_PERIOD = 30;
const TOTP_DIGITS = 6;

const base32Decode = (encoded) => {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
  const cleanedInput = encoded.toUpperCase().replace(/[^A-Z2-7]/g, "");

  const bits = [];
  for (const char of cleanedInput) {
    const val = alphabet.indexOf(char);
    if (val === -1) continue;

    for (let i = 4; i >= 0; i--) {
      bits.push((val >> i) & 1);
    }
  }

  const bytes = [];
  for (let i = 0; i + 8 <= bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j++) {
      byte = (byte << 1) | bits[i + j];
    }
    bytes.push(byte);
  }

  return new Uint8Array(bytes);
};

const intToBytes = (num) => {
  const buffer = new ArrayBuffer(8);
  const view = new DataView(buffer);
  view.setUint32(0, Math.floor(num / 0x100000000), false);
  view.setUint32(4, num >>> 0, false);
  return new Uint8Array(buffer);
};

const hmacSha1 = async (key, message) => {
  const cryptoKey = await window.crypto.subtle.importKey(
    "raw",
    key,
    { name: "HMAC", hash: "SHA-1" },
    false,
    ["sign"]
  );

  const signature = await window.crypto.subtle.sign("HMAC", cryptoKey, message);
  return new Uint8Array(signature);
};

const dynamicTruncate = (hmacResult) => {
  const offset = hmacResult[hmacResult.length - 1] & 0x0f;

  const binary =
    ((hmacResult[offset] & 0x7f) << 24) |
    ((hmacResult[offset + 1] & 0xff) << 16) |
    ((hmacResult[offset + 2] & 0xff) << 8) |
    (hmacResult[offset + 3] & 0xff);

  return binary % Math.pow(10, TOTP_DIGITS);
};

const generateTOTP = async (secret, timestamp) => {
  const time = timestamp || Math.floor(Date.now() / 1000);
  const counter = Math.floor(time / TOTP_PERIOD);

  const keyBytes = base32Decode(secret);
  const counterBytes = intToBytes(counter);
  const hmac = await hmacSha1(keyBytes, counterBytes);
  const code = dynamicTruncate(hmac);

  return code.toString().padStart(TOTP_DIGITS, "0");
};

const getTOTPRemainingSeconds = () => {
  const now = Math.floor(Date.now() / 1000);
  return TOTP_PERIOD - (now % TOTP_PERIOD);
};

const formatTOTPCode = (code) => {
  if (!code || code.length !== 6) return code;
  return `${code.slice(0, 3)} ${code.slice(3)}`;
};

function CredentialsTemplate({
  opened,
  description,
  user,
  secret,
  url,
  mfa,
  when,
}) {
  const [btnUserDisabled, setBtnUserDisabled] = useState(false);
  const [btnSecretDisabled, setBtnSecretDisabled] = useState(false);
  const [btnURLDisabled, setBtnURLDisabled] = useState(false);
  const [btnMFADisabled, setBtnMFADisabled] = useState(false);

  const [btnUserCopyContent, setBtnUserCopyContent] = useState(
    <i className="fas fa-copy"></i>
  );
  const [btnSecretCopyContent, setBtnSecretCopyContent] = useState(
    <i className="fas fa-copy"></i>
  );
  const [btnURLCopyContent, setBtnURLCopyContent] = useState(
    <i className="fas fa-copy"></i>
  );
  const [btnMFACopyContent, setBtnMFACopyContent] = useState(
    <i className="fas fa-copy"></i>
  );

  const [inputType, setInputType] = useState("password");
  const [btnRevealContent, setBtnRevealContent] = useState(
    <i className="fas fa-eye"></i>
  );
  const [contentRevealed, setContentRevealed] = useState(false);


  const [btnTOTPDisabled, setBtnTOTPDisabled] = useState(false);
  const [btnTOTPCopyContent, setBtnTOTPCopyContent] = useState(
    <i className="fas fa-copy"></i>
  );

  const [liveTOTP, setLiveTOTP] = useState("");
  const [totpRemainingSeconds, setTotpRemainingSeconds] = useState(
    getTOTPRemainingSeconds()
  );

  const [qrExpanded, setQrExpanded] = useState(false);

  function toggleQRCode() {
    setQrExpanded(!qrExpanded);
  }

  function revealData() {
    setContentRevealed(!contentRevealed);
    if (contentRevealed == false) {
      setBtnRevealContent(<i className="fas fa-eye-slash"></i>);
      setInputType("text");
    } else {
      setBtnRevealContent(<i className="fas fa-eye"></i>);
      setInputType("password");
    }
  }

  const [btnRevealFullContent, setBtnRevealFullContent] = useState(
    <>
      <i className="fas fa-eye"></i> Reveal
    </>
  );
  const [fullContentRevealed, setFullContentRevealed] = useState(false);

  function revealFullData() {
    setFullContentRevealed(!fullContentRevealed);
    if (fullContentRevealed == false) {
      setBtnRevealFullContent(
        <>
          <i className="fas fa-eye-slash"></i> Hide
        </>
      );
    } else {
      setBtnRevealFullContent(
        <>
          <i className="fas fa-eye"></i> Reveal
        </>
      );
    }
  }

  function copyToClipboard(text, setBtnDisabled, setBtnCopyContent) {
    navigator.clipboard.writeText(text);
    setBtnDisabled(true);

    /* Alert the copied text */
    setBtnCopyContent("Copied!");

    setTimeout(function () {
      setBtnDisabled(false);
      setBtnCopyContent(<i className="fas fa-copy"></i>);
    }, 3000);
  }

  function copyUserToClipboard() {
    copyToClipboard(user, setBtnUserDisabled, setBtnUserCopyContent);
  }

  function copySecretToClipboard() {
    copyToClipboard(secret, setBtnSecretDisabled, setBtnSecretCopyContent);
  }

  function copyURLToClipboard() {
    copyToClipboard(url, setBtnURLDisabled, setBtnURLCopyContent);
  }

  function copyMFAToClipboard() {
    copyToClipboard(mfa, setBtnMFADisabled, setBtnMFACopyContent);
  }

  useEffect(() => { 
    let intervalId;
  
    async function updateTOTP() {
      if (!mfa) return;
  
      try {
        const code = await generateTOTP(mfa);
        setLiveTOTP(formatTOTPCode(code));
        setTotpRemainingSeconds(getTOTPRemainingSeconds());
      } catch (err) {
        console.error("Failed to generate TOTP", err);
      }
    }
  
    if (mfa) {
      updateTOTP();
      intervalId = setInterval(updateTOTP, 1000);
    }
  
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [mfa]);

  function copyTOTPToClipboard() {
    copyToClipboard(
      liveTOTP.replace(/\s/g, ""),
      setBtnTOTPDisabled,
      setBtnTOTPCopyContent
    );
  }

  const [btnDisabled, setBtnDisabled] = useState(false);
  const [btnCopyContent, setBtnCopyContent] = useState(
    <>
      <i className="fas fa-copy"></i> Copy
    </>
  );

  function copyAllToClipboard() {
    let textToCopy = {
      username: user,
      password: secret,
    };

    if (mfa) {
      textToCopy.mfa = mfa;
    }

    if (url) {
      textToCopy.hostname = url;
    }

    navigator.clipboard.writeText(JSON.stringify(textToCopy));
    setBtnDisabled(true);

    /* Alert the copied text */
    setBtnCopyContent("Copied!");

    setTimeout(function () {
      setBtnDisabled(false);
      setBtnCopyContent(
        <>
          <i className="fas fa-copy"></i> Copy
        </>
      );
    }, 3000);
  }

  let whenBlock = "";
  let warningBlock = "";

  if (when === false) {
    whenBlock = "";
  } else if (when) {
    whenBlock = (
      <div className="mt-3 mb-3">
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-triangle"></i> This link will be
          deleted automatically {when}, save its content in a secure place.
        </div>
      </div>
    );
    warningBlock = (
      <small>
        <div className="text-danger text-center mt-3" role="alert">
          <i className="fas fa-exclamation-triangle"></i> You will need to
          re-enter the link once the progress bar runs out.
        </div>
      </small>
    );
  } else {
    whenBlock = (
      <div className="mt-3 mb-3">
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-triangle"></i> This link cannot be
          used again. Please, save its content in a secure place.
        </div>
      </div>
    );

    warningBlock = (
      <small>
        <div className="text-danger text-center mt-3" role="alert">
          <i className="fas fa-exclamation-triangle"></i> This secret will
          expire once the progress bar runs out.
        </div>
      </small>
    );
  }

  let urlBlock = "";

  if (url) {
    urlBlock = (
      <div className="input-group mb-2">
        <div className="input-group-prepend">
          <div className="btn btn-outline-primary fixed-prepend wider-prepend">
            <i className="fas fa-link"></i>
          </div>
        </div>
        <input
          type="text"
          className="form-control"
          readOnly
          id="cURL"
          value={url}
        />
        <div className="input-group-append">
          <div
            className="btn btn-outline-primary copy-data"
            disabled={btnURLDisabled}
            onClick={copyURLToClipboard}
          >
            {btnURLCopyContent}
          </div>
        </div>
      </div>
    );
  }

  let mfaBlock = "";

  if (mfa) {
    mfaBlock = (
      <>
        <div className="input-group mb-2">
          <div className="input-group-prepend">
            <div className="btn btn-outline-primary fixed-prepend wider-prepend">
              <small>2FA</small>
            </div>
          </div>
          <input
            type="text"
            className="form-control"
            readOnly
            id="c2fa"
            value={mfa}
          />
          <div className="input-group-append">
            <div
              className="btn btn-outline-primary copy-data"
              disabled={btnMFADisabled}
              onClick={copyMFAToClipboard}
            >
              {btnMFACopyContent}
            </div>
          </div>
        </div>
        <div className="w100">
          <hr />

          <div
            className="d-flex align-items-center justify-content-center mb-3"
            style={{ gap: "20px" }}
          >
            <div // SWAP COPY LIVE TOTP
              className="btn btn-outline-primary copy-data"
              disabled={btnTOTPDisabled}
              onClick={copyTOTPToClipboard}
              title="Copy live TOTP"
            >
              {btnTOTPCopyContent}
            </div>

            <div
              style={{
                fontSize: "2.4rem",
                fontWeight: "700",
                letterSpacing: "2px",
                lineHeight: "1",
              }}
            >
              {liveTOTP || "--- ---"}
            </div>

            <div
              title={`${totpRemainingSeconds}s remaining`}
              style={{
                width: "34px",
                height: "34px",
                borderRadius: "50%",
                background: `conic-gradient(#d1d1d1 ${(totpRemainingSeconds / TOTP_PERIOD) * 360}deg, #f1f1f1 0deg)`,
                position: "relative",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "5px",
                  left: "5px",
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  background: "#fff",
                }}
              ></div>
            </div>
          </div>

          <div className="text-center mb-3">
    <button
      type="button"
      className="btn btn-outline-primary btn-sm"
      onClick={toggleQRCode}
      style={{ fontSize: "0.85rem", padding: "0.3rem 0.6rem" }}
    >
      {qrExpanded ? (
        <>
          <i className="fas fa-chevron-up"></i> Hide QR code
        </>
      ) : (
        <>
          <i className="fas fa-qrcode"></i> Show QR code
        </>
      )}
    </button>
  </div>

  {qrExpanded && (
    <>
          <div className="text-center">
            <p>Scan your 2FA code directly from the QR code below:</p>
          </div>

          <div className="text-center" style={{ width: "100%" }}>
            <QRCode
              value={`otpauth://totp/${user}?secret=${mfa}`}
              fgColor="#290004"
              size="250"
              logoWidth="40"
            />
          </div>
          </>
  )}
        </div>
      </>
    );
  }

  return (
    <>
      {opened}
      {description}

      <div className="mb-3">
        <i className="fas fa-check-circle"></i> You can grab the credentials
        delivered.
      </div>

      <div className="mb-3">
        <div className="row">
          <div className="col-6">
            <button
              id="showHideMessageBtn"
              onClick={revealFullData}
              className="btn btn-primary form-control"
              type="button"
              data-toggle="collapse"
              data-target="#collapseMessage"
              aria-expanded="false"
              aria-controls="collapseMessage"
            >
              {btnRevealFullContent}
            </button>
          </div>
          <div className="col-6">
            <button
              className="btn form-control btn-outline-primary"
              type="button"
              disabled={btnDisabled}
              onClick={copyAllToClipboard}
            >
              {btnCopyContent}
            </button>
          </div>
        </div>
      </div>

      <div id="collapseMessage" className="input-group collapse">
        <div className="input-group mb-2">
          <div className="input-group-prepend">
            <div className="btn btn-outline-primary fixed-prepend wider-prepend">
              <i className="fas fa-user"></i>
            </div>
          </div>
          <input
            type="text"
            className="form-control"
            readOnly
            id="cUser"
            value={user}
          />
          <div className="input-group-append">
            <div // SWAP COPY DATA
              className="btn btn-outline-primary copy-data"
              disabled={btnUserDisabled}
              onClick={copyUserToClipboard}
            >
              {btnUserCopyContent}
            </div>
          </div>
        </div>
        <div className="input-group mb-2">
          <div className="input-group-prepend">
            <div
              className="btn btn-outline-primary fixed-prepend wider-prepend"
              onClick={revealData}
            >
              {btnRevealContent}
            </div>
          </div>
          <input
            type={inputType}
            className="form-control"
            readOnly
            id="secretData"
            value={secret}
          />
          <div className="input-group-append">
            <div
              className="btn btn-outline-primary"
              disabled={btnSecretDisabled}
              onClick={copySecretToClipboard}
            >
              {btnSecretCopyContent}
            </div>
          </div>
        </div>
        {urlBlock}
        {mfaBlock}
      </div>
      {whenBlock}
      <div id="sharepass-progressbar-timer"></div>
      {warningBlock}
    </>
  );
}

export default CredentialsTemplate;
